import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, Target, MapPin, DollarSign, Briefcase } from "lucide-react";

interface ScoringProfile {
  id: string;
  name: string;
  description: string | null;
  is_default: boolean;
  is_system: boolean;
  skill_weight: number;
  experience_weight: number;
  location_weight: number;
  salary_weight: number;
}

export default function ScoringSettings() {
  const queryClient = useQueryClient();
  const [editingProfile, setEditingProfile] = useState<ScoringProfile | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    skill_weight: 40,
    experience_weight: 25,
    location_weight: 20,
    salary_weight: 15,
  });

  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ["scoring-profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("scoring_weight_profiles")
        .select("*")
        .order("is_system", { ascending: false })
        .order("name");
      if (error) throw error;
      return data as ScoringProfile[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: Omit<ScoringProfile, "id" | "is_system">) => {
      const { error } = await supabase
        .from("scoring_weight_profiles")
        .insert(data);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scoring-profiles"] });
      toast.success("Profile created successfully");
      setIsDialogOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to create profile");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<ScoringProfile> & { id: string }) => {
      const { error } = await supabase
        .from("scoring_weight_profiles")
        .update(data)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scoring-profiles"] });
      toast.success("Profile updated successfully");
      setIsDialogOpen(false);
      setEditingProfile(null);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update profile");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("scoring_weight_profiles")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scoring-profiles"] });
      toast.success("Profile deleted successfully");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to delete profile");
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (id: string) => {
      // First unset all defaults
      await supabase
        .from("scoring_weight_profiles")
        .update({ is_default: false })
        .neq("id", id);
      // Then set the new default
      const { error } = await supabase
        .from("scoring_weight_profiles")
        .update({ is_default: true })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scoring-profiles"] });
      toast.success("Default profile updated");
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      skill_weight: 40,
      experience_weight: 25,
      location_weight: 20,
      salary_weight: 15,
    });
  };

  const handleEdit = (profile: ScoringProfile) => {
    setEditingProfile(profile);
    setFormData({
      name: profile.name,
      description: profile.description || "",
      skill_weight: profile.skill_weight,
      experience_weight: profile.experience_weight,
      location_weight: profile.location_weight,
      salary_weight: profile.salary_weight,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = () => {
    const total = formData.skill_weight + formData.experience_weight + 
                  formData.location_weight + formData.salary_weight;
    
    if (total !== 100) {
      toast.error(`Weights must total 100% (currently ${total}%)`);
      return;
    }

    if (editingProfile) {
      updateMutation.mutate({
        id: editingProfile.id,
        name: formData.name,
        description: formData.description || null,
        skill_weight: formData.skill_weight,
        experience_weight: formData.experience_weight,
        location_weight: formData.location_weight,
        salary_weight: formData.salary_weight,
      });
    } else {
      createMutation.mutate({
        name: formData.name,
        description: formData.description || null,
        is_default: false,
        skill_weight: formData.skill_weight,
        experience_weight: formData.experience_weight,
        location_weight: formData.location_weight,
        salary_weight: formData.salary_weight,
      });
    }
  };

  const totalWeight = formData.skill_weight + formData.experience_weight + 
                      formData.location_weight + formData.salary_weight;

  const WeightSlider = ({ 
    label, 
    value, 
    onChange, 
    icon: Icon 
  }: { 
    label: string; 
    value: number; 
    onChange: (v: number) => void; 
    icon: any;
  }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <Label>{label}</Label>
        </div>
        <span className="font-medium">{value}%</span>
      </div>
      <Slider
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        max={100}
        step={5}
        className="w-full"
      />
    </div>
  );

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">AI Scoring Configuration</h1>
          <p className="text-muted-foreground">
            Configure weight profiles for candidate-job matching scores
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditingProfile(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Profile
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingProfile ? "Edit Profile" : "Create Scoring Profile"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Profile Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Technical Role"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe when to use this profile..."
                  rows={2}
                />
              </div>
              
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Weight Distribution</span>
                  <Badge variant={totalWeight === 100 ? "default" : "destructive"}>
                    Total: {totalWeight}%
                  </Badge>
                </div>
                
                <WeightSlider
                  label="Skills Match"
                  value={formData.skill_weight}
                  onChange={(v) => setFormData({ ...formData, skill_weight: v })}
                  icon={Target}
                />
                <WeightSlider
                  label="Experience Level"
                  value={formData.experience_weight}
                  onChange={(v) => setFormData({ ...formData, experience_weight: v })}
                  icon={Briefcase}
                />
                <WeightSlider
                  label="Location/Work Mode"
                  value={formData.location_weight}
                  onChange={(v) => setFormData({ ...formData, location_weight: v })}
                  icon={MapPin}
                />
                <WeightSlider
                  label="Salary Alignment"
                  value={formData.salary_weight}
                  onChange={(v) => setFormData({ ...formData, salary_weight: v })}
                  icon={DollarSign}
                />
              </div>

              <Button 
                onClick={handleSubmit} 
                className="w-full"
                disabled={totalWeight !== 100 || !formData.name}
              >
                {editingProfile ? "Update Profile" : "Create Profile"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {profiles.map((profile) => (
          <Card key={profile.id} className={profile.is_default ? "border-primary" : ""}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {profile.name}
                    {profile.is_default && (
                      <Badge variant="secondary" className="text-xs">Default</Badge>
                    )}
                    {profile.is_system && (
                      <Badge variant="outline" className="text-xs">System</Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {profile.description || "No description"}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-muted-foreground" />
                  <span>Skills: {profile.skill_weight}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-muted-foreground" />
                  <span>Experience: {profile.experience_weight}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>Location: {profile.location_weight}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <span>Salary: {profile.salary_weight}%</span>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={profile.is_default}
                    onCheckedChange={() => setDefaultMutation.mutate(profile.id)}
                    disabled={profile.is_default}
                  />
                  <Label className="text-sm">Set as default</Label>
                </div>
                <div className="flex gap-1">
                  {!profile.is_system && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(profile)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteMutation.mutate(profile.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
